import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EVENTS, publish } from '@sendible/event-manager';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { fetchAllMessageIdsForRange, useGetHolidays } from '@sendible/frontendv2/src/models/calendar';
import { Container, MonthGridDayLabel, ExtraActionButton, TopRow, WeekRow } from './index.styles';
import { useGetWeeks } from './hooks';
import { DraggableCard, DroppableDay, Header, Holidays } from '..';
import { CalendarCard } from '../../../CalendarCard';
import { fullDateToMonthString, getCalendarCardMedias, getMonthRange, groupHolidaysByDate, keyFromDate } from '../../../../utils';
import { useCalendarContext } from '../../../../context';
import { useSelectionContext } from '../../../../selectionContext';
import { CreationButton } from '../CreationButton';

interface Props {
  onRender: () => void;
}

type MonthlyCardProps = {
  message: CalendarMessage;
  dayKey: string;
  density: CalendarDensity;
  isWorkspaceMode: boolean;
  openSidebar: (type: 'message' | 'day', day: CalendarMonthString, messageId?: CalendarMessage['id']) => void;
};

const MonthlyDraggableCard = memo(({ message, dayKey, density, isWorkspaceMode, openSidebar }: MonthlyCardProps) => {
  const handleClick = useCallback(
    () => openSidebar('message', fullDateToMonthString(message.sendDateLocalized), message.id),
    [openSidebar, message.sendDateLocalized, message.id]
  );

  return (
    <DraggableCard
      message={message}
      dayKey={dayKey}
    >
      <CalendarCard
        id={message.id}
        onClick={handleClick}
        deliveryStatus={message.deliveryStatus}
        postAt={message.sendDateLocalized}
        destinations={message.destinations || []}
        status={message.status}
        medias={getCalendarCardMedias(message.body)}
        recurrence={message.recurrence}
        title={message.body?.messageText || message.subject}
        type={density === 'comfortable' ? 'monthly' : 'compact'}
        workspace={isWorkspaceMode ? message.author || message.workspace : message.workspace}
      />
    </DraggableCard>
  );
});

MonthlyDraggableCard.displayName = 'MonthlyDraggableCard';

export const MonthlyGrid = ({ onRender }: Props) => {
  const user = useBridgeUser();
  const { density, openSidebar, todayDate, userWeekStartDay, showHolidays, currentDate, filterParams } = useCalendarContext();
  const { failedMessageCountByDate, messageByDate, messageCountByDate, visibleFailedCountByDate, weeks } = useGetWeeks();
  const { data: holidays, isFetching, isLoading } = useGetHolidays(currentDate, userWeekStartDay, { enabled: showHolidays });
  const { t } = useTranslation(['common_terms', 'calendar']);
  const { clearVisibleMessageIds, setSelectAllCapability, setVisibleMessageIds } = useSelectionContext();

  const holidaysByDate = useMemo(() => groupHolidaysByDate(holidays), [holidays]);
  const isGettingHolidays = showHolidays && isFetching && isLoading;

  const isWorkspaceMode = Boolean(user?.isWorkspaceMode);

  useEffect(() => onRender(), []);

  useEffect(() => {
    setVisibleMessageIds(
      'monthly-grid',
      Object.values(messageByDate)
        .flat()
        .map((message) => message.id)
    );
  }, [messageByDate, setVisibleMessageIds]);

  useEffect(() => () => clearVisibleMessageIds('monthly-grid'), [clearVisibleMessageIds]);

  // "Select all" for the monthly view means every post in the visible month, not just the
  // ones rendered per day cell (the "Posts per day" setting truncates each day, see
  // POSTS_PER_DAY_LIMITS) — fetch the full range on demand
  // rather than only ever selecting what happened to already be loaded.
  useEffect(() => {
    const totalCount = Object.values(messageCountByDate).reduce((sum, count) => sum + count, 0);

    setSelectAllCapability({
      totalCount,
      fetchAllIds: async (signal) => {
        const [startISO, endISO] = getMonthRange(currentDate, userWeekStartDay);

        return fetchAllMessageIdsForRange({
          accessToken: user?.accessToken ?? '',
          userId: user?.userId ?? 0,
          email: user?.email ?? '',
          endISO,
          filterParams,
          isSwitchedUser: user?.isSwitchedUser,
          isWorkspaceMode,
          signal,
          startISO,
          username: user?.username,
        });
      },
    });

    return () => setSelectAllCapability(null);
  }, [messageCountByDate, currentDate, userWeekStartDay, filterParams, user, setSelectAllCapability]);

  return (
    <>
      <Header userWeekStartDay={userWeekStartDay} />

      <Container>
        {weeks.map((week, wi) => (
          <WeekRow
            key={wi}
            data-testid={`monthly-view-week-row-${wi}`}
          >
            {week.map((cell, di) => {
              const dayKey = keyFromDate(cell.date);
              const dayMessages = messageByDate[dayKey] ?? [];
              const totalCount = messageCountByDate[dayKey] ?? 0;
              const hiddenCount = Math.max(0, totalCount - dayMessages.length);
              const dayFailed = failedMessageCountByDate[dayKey] ?? 0;
              const failedCount = Math.max(0, dayFailed - (visibleFailedCountByDate[dayKey] ?? 0));
              const dayNumber = cell.date.getDate();
              const isToday = dayKey === todayDate;
              const isPastDay = dayKey < todayDate;
              const dayHolidays = holidaysByDate[dayKey] ?? [];
              const showHoliday = !isPastDay && showHolidays && dayHolidays.length > 0;

              return (
                <DroppableDay
                  data-testid={`monthly-view-day-container-${di}`}
                  key={di}
                  dayKey={dayKey}
                  isToday={isToday}
                  view="monthly"
                >
                  <TopRow>
                    <MonthGridDayLabel
                      data-testid={`monthly-view-day-label-${di}`}
                      aria-label={`Day ${dayNumber}${cell.isCurrentMonth ? '' : ' (other month)'}`}
                      $isToday={isToday}
                    >
                      {dayNumber}
                      {dayNumber === 1 && <strong> {cell.date.toLocaleString(undefined, { month: 'short' })}</strong>}
                    </MonthGridDayLabel>
                    {!isPastDay && (
                      <CreationButton
                        onClick={() => publish(EVENTS.OPEN_COMPOSE, { mode: 'new', date: dayKey, offset: user.timezone })}
                        view="monthly"
                      />
                    )}
                  </TopRow>

                  {showHoliday && (
                    <Holidays
                      isLoading={isGettingHolidays}
                      holidays={dayHolidays}
                      onClick={(text: string, image: string) =>
                        publish(EVENTS.OPEN_COMPOSE, {
                          mode: 'new',
                          date: dayKey,
                          offset: user.timezone,
                          preFillText: text,
                          preFillImage: image,
                        })
                      }
                    />
                  )}

                  {dayMessages.map((message) => (
                    <MonthlyDraggableCard
                      key={message.id}
                      message={message}
                      dayKey={dayKey}
                      density={density}
                      isWorkspaceMode={isWorkspaceMode}
                      openSidebar={openSidebar}
                    />
                  ))}

                  {(hiddenCount >= 1 || failedCount >= 1) && (
                    <ExtraActionButton
                      data-testid={`monthly-view-more-button-${di}`}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openSidebar('day', dayKey);
                      }}
                      aria-label={t('show_more_posts_for_day', {
                        ns: 'calendar',
                        count: hiddenCount,
                        date: cell.date.toLocaleDateString(undefined, { day: 'numeric', month: 'long' }),
                      })}
                    >
                      {hiddenCount >= 1 && `+${hiddenCount} ${t('more', { ns: 'common_terms' })}`}
                      <b>{failedCount >= 1 && `${failedCount} ${t('Failed', { ns: 'calendar' }).toLowerCase()}`}</b>
                    </ExtraActionButton>
                  )}
                </DroppableDay>
              );
            })}
          </WeekRow>
        ))}
      </Container>
    </>
  );
};
